import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@app-evolution-cms/element-ui';

import { Group } from '../_states/sidebar-groups';
import { CustomizationToolbar } from './customization-toolbar';
import { MenuGroup } from './menu-group';
import { MenuItemWithControls } from './menu-item-with-controls';

import { useUserState } from '../../users/_states/user';
import { useSidebarGroupsToggleState } from '../_states/sidebar-groups-toggle';
import { useCustomGroups } from './use-custom-groups';
import {
  useAssignedModuleIds,
  useClaimsByModuleId,
  useFilteredClaims,
} from './use-filtered-claims';

import { type Claim } from '../../users/_api/request.type';
import { MODULE_CLAIM } from '../../users/_constants/module-claim';

const ID_PREFIX = 'PER-navigation-sidebarContent';

export const MODULE_EXCLUSIONS = {
  CORE: [
    MODULE_CLAIM.eventsApplicationUrl,
    MODULE_CLAIM.featureFlags,
    MODULE_CLAIM.media,
    MODULE_CLAIM.modules,
    MODULE_CLAIM.notificationCategories,
    MODULE_CLAIM.notificationCenterConfiguration,
    MODULE_CLAIM.notificationCenterFilters,
    MODULE_CLAIM.notificationCenterTemplates,
    MODULE_CLAIM.ruleEvents,
    MODULE_CLAIM.ruleGroups,
    MODULE_CLAIM.rules,
    MODULE_CLAIM.targetGroups,
  ],
  NOTIFICATION_CENTER: [
    MODULE_CLAIM.notificationCategories,
    MODULE_CLAIM.notificationCenterConfiguration,
    MODULE_CLAIM.notificationCenterFilters,
    MODULE_CLAIM.notificationCenterTemplates,
  ],
  UTILITIES: [MODULE_CLAIM.media],
  LABORATORY: [MODULE_CLAIM.targetGroups, MODULE_CLAIM.featureFlags],
  RULES: [
    MODULE_CLAIM.rules,
    MODULE_CLAIM.ruleGroups,
    MODULE_CLAIM.ruleEvents,
    MODULE_CLAIM.eventsApplicationUrl,
  ],
  MODULE_BUILDER: [MODULE_CLAIM.modules],
};

interface NavModulesProps {
  searchValue: string;
}

export function NavModules({ searchValue }: NavModulesProps) {
  const groupsCollapsedState = useSidebarGroupsToggleState((s) => s.groups);
  const groupsCollapsedStateUpdate = useSidebarGroupsToggleState((s) => s.update);

  const userClaims = useUserState((s) => s.user?.claims) ?? [];

  const {
    customGroups,
    isEditMode,
    newGroupLabel,
    addGroup,
    assignModuleToGroup,
    removeGroup,
    setNewGroupLabel,
    toggleEditMode,
  } = useCustomGroups();

  const getGroupOpenState = ({ id }: { id: string }) => {
    return groupsCollapsedState[id] !== undefined ? groupsCollapsedState[id] : true;
  };

  const setGroupOpenState = ({ id, isOpen }: { id: string; isOpen: boolean }) => {
    groupsCollapsedStateUpdate({ groups: { ...groupsCollapsedState, [id]: isOpen } });
  };

  const assignedModuleIds = useAssignedModuleIds({ customGroups });
  const claimsByModuleId = useClaimsByModuleId({ claims: userClaims });

  const coreModuleClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    excludedModules: MODULE_EXCLUSIONS.CORE,
  });

  const notificationCenterClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    includedModules: MODULE_EXCLUSIONS.NOTIFICATION_CENTER,
  });

  const utilitiesClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    includedModules: MODULE_EXCLUSIONS.UTILITIES,
  });

  const laboratoryClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    includedModules: MODULE_EXCLUSIONS.LABORATORY,
  });

  const rulesClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    includedModules: MODULE_EXCLUSIONS.RULES,
  });

  const moduleBuilderClaims = useFilteredClaims({
    claims: userClaims,
    assignedModuleIds,
    includedModules: MODULE_EXCLUSIONS.MODULE_BUILDER,
  });

  const normalizedSearchValue = searchValue.trim().toLocaleLowerCase();

  const matchesSearch = (claim: Claim) => {
    if (!normalizedSearchValue) {
      return true;
    }

    const searchableClaim =
      `${claim.module.label} ${claim.role} ${claim.module.id}`.toLocaleLowerCase();
    return searchableClaim.includes(normalizedSearchValue);
  };

  const isClaim = (claim: Claim | undefined): claim is Claim => Boolean(claim);

  const filteredCoreModuleClaims = coreModuleClaims.filter(matchesSearch);
  const filteredNotificationCenterClaims = notificationCenterClaims.filter(matchesSearch);
  const filteredUtilitiesClaims = utilitiesClaims.filter(matchesSearch);
  const filteredLaboratoryClaims = laboratoryClaims.filter(matchesSearch);
  const filteredRulesClaims = rulesClaims.filter(matchesSearch);
  const filteredModuleBuilderClaims = moduleBuilderClaims.filter(matchesSearch);

  const renderMenuItemWithControls = ({
    claim,
    assignedGroupId,
  }: {
    claim: Claim;
    assignedGroupId?: string;
  }) => (
    <MenuItemWithControls
      key={claim.module.id}
      claim={claim}
      isEditMode={isEditMode}
      assignedGroupId={assignedGroupId}
      customGroups={customGroups}
      onAssignToGroup={assignModuleToGroup}
    />
  );

  return (
    <>
      <CustomizationToolbar
        id={ID_PREFIX}
        isEditMode={isEditMode}
        newGroupLabel={newGroupLabel}
        onToggleEditMode={toggleEditMode}
        onGroupLabelChange={setNewGroupLabel}
        onAddGroup={addGroup}
      />

      <div
        className={cn('rounded-3xl rounded-t-none', {
          'bg-muted pb-3': isEditMode,
          'pb-4': customGroups.length === 0 && isEditMode,
        })}
      >
        <AnimatePresence initial={false}>
          {customGroups.map((group: Group) => {
            const groupModules = group.moduleIds
              .map((id: string) => claimsByModuleId.get(id))
              .filter(isClaim)
              .filter(matchesSearch);

            if (groupModules.length === 0 && normalizedSearchValue) {
              return null;
            }

            return (
              <motion.div
                key={group.id}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <MenuGroup
                  id={`${ID_PREFIX}-custom-${group.id}`}
                  isEditMode={isEditMode}
                  isOpen={getGroupOpenState({ id: group.id })}
                  label={group.label}
                  onDeleteGroup={() => removeGroup({ groupId: group.id })}
                  onOpenChange={(isOpen) => setGroupOpenState({ id: group.id, isOpen })}
                >
                  {groupModules.map((claim) =>
                    renderMenuItemWithControls({ claim, assignedGroupId: group.id }),
                  )}
                </MenuGroup>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {filteredCoreModuleClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-modulesSection`}
          label="Módulos"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-modulesSection` })}
          onOpenChange={(isOpen) =>
            setGroupOpenState({ id: `${ID_PREFIX}-modulesSection`, isOpen })
          }
        >
          {filteredCoreModuleClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}

      {filteredNotificationCenterClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-notificationCenterSection`}
          label="Centro de Notificaciones"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-notificationCenterSection` })}
          onOpenChange={(isOpen) =>
            setGroupOpenState({ id: `${ID_PREFIX}-notificationCenterSection`, isOpen })
          }
        >
          {filteredNotificationCenterClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}

      {filteredUtilitiesClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-utilsSection`}
          label="Utilidades"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-utilsSection` })}
          onOpenChange={(isOpen) => setGroupOpenState({ id: `${ID_PREFIX}-utilsSection`, isOpen })}
        >
          {filteredUtilitiesClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}

      {filteredLaboratoryClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-laboratorySection`}
          label="Feature Manager"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-laboratorySection` })}
          onOpenChange={(isOpen) =>
            setGroupOpenState({ id: `${ID_PREFIX}-laboratorySection`, isOpen })
          }
        >
          {filteredLaboratoryClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}

      {filteredRulesClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-rulesSection`}
          label="Rules Engine Manager"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-rulesSection` })}
          onOpenChange={(isOpen) => setGroupOpenState({ id: `${ID_PREFIX}-rulesSection`, isOpen })}
        >
          {filteredRulesClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}

      {filteredModuleBuilderClaims.length > 0 ? (
        <MenuGroup
          id={`${ID_PREFIX}-moduleBuilderSection`}
          label="Estudio"
          isOpen={getGroupOpenState({ id: `${ID_PREFIX}-moduleBuilderSection` })}
          onOpenChange={(isOpen) =>
            setGroupOpenState({ id: `${ID_PREFIX}-moduleBuilderSection`, isOpen })
          }
        >
          {filteredModuleBuilderClaims.map((claim) => renderMenuItemWithControls({ claim }))}
        </MenuGroup>
      ) : null}
    </>
  );
}
